import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import type { Service } from '@/types/service';
import { ServiceType } from '@/types/api';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea, useToast } from '@nimbitlabs/ui';

const serviceInfoSchema = z.object({
  name: z.string().min(1, 'Service name is required').max(200, 'Service name must be 200 characters or less'),
  description: z.string().min(1, 'Description is required').max(1000, 'Description must be 1000 characters or less'),
  type: z.enum([ServiceType.Api, ServiceType.Microservice, ServiceType.Database, ServiceType.Frontend]),
  teamId: z.string().min(1, 'Please select a team'),
});

type ServiceInfoFormData = z.infer<typeof serviceInfoSchema>;

interface EditServiceInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: Service | null;
  onServiceUpdated?: () => void;
}

export const EditServiceInfoModal: React.FC<EditServiceInfoModalProps> = ({ open, onOpenChange, service, onServiceUpdated }) => {
  const { updateService: updateServiceFn } = useServicesStore();
  const { teams: allTeams } = useTeamsStore();
  const { toast } = useToast();
  
  const form = useForm<ServiceInfoFormData>({
    resolver: zodResolver(serviceInfoSchema),
    defaultValues: {
      name: '',
      description: '',
      type: ServiceType.Api,
      teamId: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (service && open) {
      form.reset({
        name: service.name,
        description: service.description,
        type: service.type,
        teamId: service.teamId,
      });
    }
  }, [service, open, form]);

  const onSubmit = async (data: ServiceInfoFormData) => {
    if (!service) return;
    
    try {
      await updateServiceFn(service.id, data);
      toast({ title: 'Success', description: 'Service updated successfully' });
      onServiceUpdated?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating service info:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update service',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Service Information</DialogTitle>
          <DialogDescription>
            Update the basic information for this service.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="My Service" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description *</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Describe what this service does..." rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ServiceType.Api}>API</SelectItem>
                      <SelectItem value={ServiceType.Microservice}>Microservice</SelectItem>
                      <SelectItem value={ServiceType.Database}>Database</SelectItem>
                      <SelectItem value={ServiceType.Frontend}>Frontend</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="teamId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Team *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {allTeams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Updating...' : 'Update Service'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
